import { Page } from "@/app/shared/components/page";
import { CardOverviewGrid } from "./components/CardOverviewGrid";
import { CardProfilesPanel } from "./components/CardProfilesPanel";
import { CardTransactionsTab } from "./components/CardTransactionsTab";
import { PeriodSummaryHeader } from "./components/PeriodSummaryHeader";
import { CategoryBreakdown } from "./components/CategoryBreakdown";
import { BillsView } from "./components/BillsView";
import { useCreditCardOverview } from "@/hooks/api";
import { cn } from "@/lib/utils";
import { ChevronRight, RefreshCw } from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import { Button } from "@groot/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { addMonths, getCurrentMonthString } from "@/lib/date.utils";
import { useBillingMonth } from "./hooks/useBillingMonth";
import type { CreditCardStatus } from "@/types";

type TabId = "overview" | "bills" | "transactions" | "cards";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "bills", label: "Bills" },
  { id: "transactions", label: "Transactions" },
  { id: "cards", label: "Cards" },
];

/** Parses ?status= into a valid account-status scope (default: active). */
function parseStatusParam(value: string): CreditCardStatus {
  return value === "archived" || value === "all" ? value : "active";
}

/** Status filter mirroring the Accounts page's All/Active/Archived select. */
function StatusFilter({
  value,
  onChange,
}: {
  value: CreditCardStatus;
  onChange: (v: CreditCardStatus) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as CreditCardStatus)}>
      <SelectTrigger
        className="h-8 w-full shrink-0 sm:w-[110px]"
        aria-label="Filter cards by status"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="active">Active</SelectItem>
        <SelectItem value="archived">Archived</SelectItem>
        <SelectItem value="all">All</SelectItem>
      </SelectContent>
    </Select>
  );
}

export function CreditCards() {
  const [monthParam, setMonthParam] = useBillingMonth();
  const [tabParam, setTabParam] = useQueryState("tab", parseAsString.withDefault("overview"));
  const [statusParam, setStatusParam] = useQueryState(
    "status",
    parseAsString.withDefault("active"),
  );
  const status = parseStatusParam(statusParam);
  const activeTab = (TABS.find((t) => t.id === tabParam)?.id ?? "overview") as TabId;

  const { data: overview, isLoading, error, refetch } = useCreditCardOverview(monthParam, status);

  const isCurrentMonth = monthParam === getCurrentMonthString();

  // The transactions tab is a full-bleed register (like /transactions): it
  // escapes the Page wrapper's max-width + shell padding and brings its own
  // account sidebar.
  if (activeTab === "transactions") {
    return (
      <div className="h-[calc(100vh-3.5rem)] bg-background lg:h-screen">
        {/* Header: billing-period switcher */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-background py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMonthParam(addMonths(monthParam, -1))}
              className="size-8 p-0"
              aria-label="Previous billing month"
            >
              <ChevronRight className="size-4 rotate-180" />
            </Button>
            <h1 className="min-w-[160px] text-center text-lg font-medium">
              {overview?.monthDisplay ?? monthParam}
            </h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMonthParam(addMonths(monthParam, 1))}
              disabled={isCurrentMonth}
              className="size-8 p-0"
              aria-label="Next billing month"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <StatusFilter value={status} onChange={setStatusParam} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="h-8"
              aria-label="Refresh"
            >
              <RefreshCw className={cn("size-4", isLoading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4 overflow-x-auto" aria-label="Credit card views">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTabParam(tab.id)}
                className={cn(
                  "whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium",
                  activeTab === tab.id
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
                aria-current={activeTab === tab.id ? "page" : undefined}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <CardTransactionsTab month={monthParam} />
      </div>
    );
  }

  return (
    <Page>
      {/* Header: billing-period switcher (mirrors Budgets' month nav) */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-background px-0 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMonthParam(addMonths(monthParam, -1))}
            className="size-8 p-0"
            aria-label="Previous billing month"
          >
            <ChevronRight className="size-4 rotate-180" />
          </Button>
          <h1 className="min-w-[160px] text-center text-lg font-medium">
            {overview?.monthDisplay ?? monthParam}
          </h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMonthParam(addMonths(monthParam, 1))}
            disabled={isCurrentMonth}
            className="size-8 p-0"
            aria-label="Next billing month"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <StatusFilter value={status} onChange={setStatusParam} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="h-8"
            aria-label="Refresh"
          >
            <RefreshCw className={cn("size-4", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex gap-4 overflow-x-auto" aria-label="Credit card views">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTabParam(tab.id)}
              className={cn(
                "whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium",
                activeTab === tab.id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
              aria-current={activeTab === tab.id ? "page" : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Error loading credit cards</p>
          <p className="mt-1 text-xs text-destructive">
            {error instanceof Error ? error.message : "An error occurred"}
          </p>
        </div>
      )}

      <div className="py-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            <PeriodSummaryHeader overview={overview} isLoading={isLoading} month={monthParam} />
            <CardOverviewGrid overview={overview} isLoading={isLoading} month={monthParam} />
            <CategoryBreakdown overview={overview} isLoading={isLoading} month={monthParam} />
          </div>
        )}
        {activeTab === "bills" && <BillsView month={monthParam} status={status} />}
        {activeTab === "cards" && <CardProfilesPanel status={status} />}
      </div>
    </Page>
  );
}
